import { OrderedMap } from 'immutable';

export function groupBy<TItem, TKey>(source: TItem[], keyGetter: (item: TItem) => TKey): OrderedMap<TKey, TItem[]> {
    let keys = OrderedMap<string, TKey>();
    let values = OrderedMap<string, TItem[]>();

    source.forEach((item) => {
        const objectKey = keyGetter(item);
        const key = JSON.stringify(objectKey);

        const groupValues = values.get(key);
        if (!groupValues) {
            values = values.set(key, [item]);
        } else {
            groupValues.push(item);
        }

        const groupKeys = keys.get(key);
        if (!groupKeys) {
            keys = keys.set(key, objectKey);
        }
    });
    return values.mapKeys((key) => keys.get(key) as TKey);
}

export function innerJoin<T, TTarget, TKey, TResult>(
    leftArray: Array<T>,
    rightArray: Array<TTarget>,
    leftKeyGetter: (item: T) => TKey,
    rightKeyGetter: (item: TTarget) => TKey,
    resultSelector: (left: T, right: TTarget) => TResult
): Array<TResult> {
    let map = OrderedMap<TKey, { left: T; right: TTarget | null }>();
    for (const leftItem of leftArray) {
        map = map.set(leftKeyGetter(leftItem), { left: leftItem, right: null });
    }
    for (const rightItem of rightArray) {
        const temp = map.get(rightKeyGetter(rightItem));
        if (temp) temp.right = rightItem;
    }
    return (
        map
            .valueSeq()
            .filter((x) => x.right !== null)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .map((x) => resultSelector(x.left, x.right!))
            .toArray()
    );
}

export function leftJoin<T, TTarget, TKey, TResult>(
    leftArray: Array<T>,
    rightArray: Array<TTarget>,
    leftKeyGetter: (item: T) => TKey,
    rightKeyGetter: (item: TTarget) => TKey,
    resultSelector: (left: T, right: TTarget | null) => TResult
): Array<TResult> {
    let map = OrderedMap<string, { left: T; right: TTarget | null }>();
    for (const leftItem of leftArray) {
        map = map.set(JSON.stringify(leftKeyGetter(leftItem)), {
            left: leftItem,
            right: null,
        });
    }
    for (const rightItem of rightArray) {
        const rightKey = JSON.stringify(rightKeyGetter(rightItem));
        const temp = map.get(rightKey);
        if (temp) temp.right = rightItem;
    }
    return map
        .valueSeq()
        .map((x) => resultSelector(x.left, x.right))
        .toArray();
}

export function fullJoin<T, TTarget, TKey, TResult>(
    leftArray: Array<T>,
    rightArray: Array<TTarget>,
    leftKeyGetter: (item: T) => TKey,
    rightKeyGetter: (item: TTarget) => TKey,
    resultSelector: (left: T | null, right: TTarget | null) => TResult
): Array<TResult> {
    let map = OrderedMap<string, { left: T | null; right: TTarget | null }>();
    for (const leftItem of leftArray) {
        map = map.set(JSON.stringify(leftKeyGetter(leftItem)), {
            left: leftItem,
            right: null,
        });
    }
    for (const rightItem of rightArray) {
        const rightKey = JSON.stringify(rightKeyGetter(rightItem));
        const temp = map.get(rightKey);
        if (temp) temp.right = rightItem;
        else map = map.set(rightKey, { left: null, right: rightItem });
    }
    return map
        .valueSeq()
        .map((x) => resultSelector(x.left, x.right))
        .toArray();
}

export function toDictionary<T, TKey, TValue>(
    source: T[],
    keyGetter: (item: T) => TKey,
    valueGetter: (item: T) => TValue
): OrderedMap<TKey, TValue> {
    let result = OrderedMap<TKey, TValue>();
    source.forEach((item) => {
        const key = keyGetter(item);
        const value = valueGetter(item);
        result = result.set(key, value);
    });
    return result;
}
